import { useEffect, useRef, useState } from "react";
import { Annotation } from "@/lib/indexedDB";
import { AnnotationConfig } from "./AnnotationPanel";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Trash2, Edit3 } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

interface AnnotationLayerProps {
  pageNumber: number;
  annotations: Annotation[];
  scale: number;
  isDrawing: boolean;
  config: AnnotationConfig | null;
  onAnnotationCreate: (annotation: Omit<Annotation, "id" | "pdfUrl">) => void;
  onAnnotationDelete: (id: string) => void;
  onAnnotationUpdate: (id: string, note: string) => void;
  containerWidth: number;
  containerHeight: number;
}

const colorMap = {
  red: "#ef4444",
  yellow: "#facc15",
  green: "#22c55e",
  blue: "#3b82f6",
};

export const AnnotationLayer = ({
  pageNumber,
  annotations,
  scale,
  isDrawing,
  config,
  onAnnotationCreate,
  onAnnotationDelete,
  onAnnotationUpdate,
  containerWidth,
  containerHeight,
}: AnnotationLayerProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [startPos, setStartPos] = useState<{ x: number; y: number } | null>(
    null
  );
  const [currentPos, setCurrentPos] = useState<{ x: number; y: number } | null>(
    null
  );
  const [selectedAnnotation, setSelectedAnnotation] =
    useState<Annotation | null>(null);
  const [editingNote, setEditingNote] = useState("");
  const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isMobile = useIsMobile();

  // 使用 ref 保存狀態供原生事件監聽器使用
  const drawingStateRef = useRef({
    isMouseDown: false,
    startPos: null as { x: number; y: number } | null,
    currentPos: null as { x: number; y: number } | null,
  });

  // Draw existing annotations and current drawing
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw existing annotations
    annotations
      .filter((ann) => ann.pageNumber === pageNumber)
      .forEach((ann) => {
        ctx.fillStyle = colorMap[ann.color];
        ctx.globalAlpha = ann.opacity / 100;

        const x = ann.x * scale;
        const y = ann.y * scale;
        const width = ann.width * scale;
        const height = ann.height * scale;

        ctx.fillRect(x, y, width, height);
      });

    // Draw current drawing rectangle
    if (isMouseDown && startPos && currentPos && config) {
      ctx.fillStyle = colorMap[config.color];
      ctx.globalAlpha = config.opacity / 100;

      const x = Math.min(startPos.x, currentPos.x) * scale;
      const y = Math.min(startPos.y, currentPos.y) * scale;
      const width = Math.abs(currentPos.x - startPos.x) * scale;
      const height = Math.abs(currentPos.y - startPos.y) * scale;

      ctx.fillRect(x, y, width, height);
    }

    ctx.globalAlpha = 1;
  }, [
    annotations,
    pageNumber,
    scale,
    isMouseDown,
    startPos,
    currentPos,
    config,
    containerWidth,
    containerHeight,
  ]);

  const getPosition = (e: React.MouseEvent | React.TouchEvent) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return null;

    let clientX: number, clientY: number;
    if ("touches" in e) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    return {
      x: (clientX - rect.left) / scale,
      y: (clientY - rect.top) / scale,
    };
  };

  const handleStart = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing || !config) return;
    e.preventDefault();
    // Avoid triggering parent click/toggle

    e.stopPropagation?.();

    const pos = getPosition(e);
    if (!pos) return;

    setIsMouseDown(true);
    setStartPos(pos);
    setCurrentPos(pos);

    // 同步到 ref
    drawingStateRef.current = {
      isMouseDown: true,
      startPos: pos,
      currentPos: pos,
    };
  };

  const handleMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isMouseDown || !config) return;
    e.preventDefault();

    e.stopPropagation?.();

    const pos = getPosition(e);
    if (!pos) return;

    setCurrentPos(pos);
    // 同步到 ref
    drawingStateRef.current.currentPos = pos;
  };

  const handleEnd = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isMouseDown || !startPos || !config) return;

    e.preventDefault();

    e.stopPropagation?.();

    const pos = getPosition(e);
    if (!pos) return;

    const x = Math.min(startPos.x, pos.x);
    const y = Math.min(startPos.y, pos.y);
    const width = Math.abs(pos.x - startPos.x);
    const height = Math.abs(pos.y - startPos.y);

    if (width > 5 && height > 5) {
      onAnnotationCreate({
        pageNumber,
        type: config.type,
        color: config.color,
        opacity: config.opacity,
        x,
        y,
        width,
        height,
        currentScreen: containerWidth, // 傳遞當前頁面寬度
      });
    }

    setIsMouseDown(false);
    setStartPos(null);
    setCurrentPos(null);

    // 重置 ref
    drawingStateRef.current = {
      isMouseDown: false,
      startPos: null,
      currentPos: null,
    };
  };

  const handleCancel = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    setIsMouseDown(false);
    setStartPos(null);
    setCurrentPos(null);

    // 重置 ref
    drawingStateRef.current = {
      isMouseDown: false,
      startPos: null,
      currentPos: null,
    };
  };

  // 使用原生事件監聽器在捕獲階段處理觸控事件（比 Hammer.js 更早）
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !isDrawing) return;

    const handleNativeTouchEnd = (e: TouchEvent) => {
      const state = drawingStateRef.current;
      if (!state.isMouseDown || !state.startPos || !config) return;

      e.preventDefault();
      e.stopPropagation();

      // 計算結束位置
      const rect = canvas.getBoundingClientRect();
      const touch = e.changedTouches[0];
      const endPos = {
        x: (touch.clientX - rect.left) / scale,
        y: (touch.clientY - rect.top) / scale,
      };

      const x = Math.min(state.startPos.x, endPos.x);
      const y = Math.min(state.startPos.y, endPos.y);
      const width = Math.abs(endPos.x - state.startPos.x);
      const height = Math.abs(endPos.y - state.startPos.y);

      if (width > 5 && height > 5) {
        onAnnotationCreate({
          pageNumber,
          type: config.type,
          color: config.color,
          opacity: config.opacity,
          x,
          y,
          width,
          height,
          currentScreen: containerWidth, // 傳遞當前頁面寬度
        });
      }

      // 重置狀態
      setIsMouseDown(false);
      setStartPos(null);
      setCurrentPos(null);
      drawingStateRef.current = {
        isMouseDown: false,
        startPos: null,
        currentPos: null,
      };
    };

    // 在捕獲階段監聽，比 Hammer.js 更早處理
    canvas.addEventListener("touchend", handleNativeTouchEnd, {
      capture: true,
      passive: false,
    });

    return () => {
      canvas.removeEventListener("touchend", handleNativeTouchEnd, {
        capture: true,
      });
    };
  }, [
    isDrawing,
    config,
    scale,
    pageNumber,
    onAnnotationCreate,
    containerWidth,
  ]);

  const handleAnnotationClick = (ann: Annotation) => {
    setSelectedAnnotation(ann);
    setEditingNote(ann.note || "");
  };

  return (
    <>
      <canvas
        ref={canvasRef}
        width={containerWidth}
        height={containerHeight}
        className="absolute inset-0 z-20 pointer-events-auto"
        style={{
          cursor: isDrawing ? "crosshair" : "default",
          touchAction: isDrawing ? "none" : "auto", // 在繪製模式下禁用瀏覽器手勢
          boxSizing: "border-box", // 確保邊框不會影響尺寸計算
        }}
        onMouseDown={handleStart}
        onMouseMove={handleMove}
        onMouseUp={handleEnd}
        onMouseLeave={handleCancel}
        onTouchStart={handleStart}
        onTouchMove={handleMove}
        onTouchEnd={handleEnd}
        onTouchCancel={handleCancel}
      />

      {/* Annotation markers */}
      {annotations
        .filter((ann) => ann.pageNumber === pageNumber)
        .map((ann) => {
          return (
            <div
              key={ann.id}
              className="absolute cursor-pointer z-30"
              style={{
                left: `${ann.x * scale}px`,
                top: `${ann.y * scale}px`,
                width: `${ann.width * scale}px`,
                height: `${ann.height * scale}px`,
              }}
              onClick={() => handleAnnotationClick(ann)}
            />
          );
        })}

      {/* Edit dialog */}
      {selectedAnnotation && (
        <div
          className="absolute top-4 right-4 z-50 w-80 rounded-lg border bg-card p-4 shadow-lg"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onTouchStart={(e) => {
            e.stopPropagation();
          }}
          onTouchEnd={(e) => {
            e.stopPropagation();
          }}
        >
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">Edit Highlight</h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSelectedAnnotation(null)}
              >
                ×
              </Button>
            </div>
            <Textarea
              placeholder="Add a note..."
              value={editingNote}
              onChange={(e) => setEditingNote(e.target.value)}
              rows={3}
            />
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={() => {
                  onAnnotationUpdate(selectedAnnotation.id, editingNote);
                  setSelectedAnnotation(null);
                }}
              >
                <Edit3 className="mr-2 h-4 w-4" />
                Save
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => {
                  onAnnotationDelete(selectedAnnotation.id);
                  setSelectedAnnotation(null);
                }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
